import { FormattedMessage, injectIntl, defineMessages, IntlShape } from "react-intl";
import React from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";
import { IntlProvider } from "react-intl";
import { getLocale } from "../locales";
import { getMessages } from "../getMessages";
import "./Registration.scss";
import Logo from "../app/Logo.react";
import { defaultOptions } from "../app/auth.js";
import Auth0Variables from "../app/auth0-variables";
import { displayError } from "../app/errorDisplayer";
import initRelayLayer from "../initRelayLayer";
import MeQuery from "../queries/MeQuery";
import RelayQueryRequest from "react-relay/lib/RelayQueryRequest";
import { Query } from "react-relay/lib/fromGraphQL";
import EventTracker from "../app/EventTracker";
import Deferred from "../fw/deferred";
import fetch from "node-fetch";
import FormData from "form-data";
import { LoadingLine } from "../loadingLine/renderLoadingPanel.react";
import auth0 from "auth0-js";
import "./RegistrationForm.scss"; // Import the CSS file from TestAuthForm
import config from '../../common/config';

/* eslint react/jsx-sort-props: 0 */

export const messages = defineMessages({
	newsletter: { id: "registration.newsletter", defaultMessage: "Receive monthly newsletter" },
	agree1: { id: "registration.signupTerm1", defaultMessage: "I agree to the" },
	agree2: { id: "registration.signupTerm2", defaultMessage: "license agreement" },
	// Registration form
	emailRegistrationTitle: { id: "registration.emailRegistrationTitle", defaultMessage: "Register with email" },
	emailLabel: { id: "registration.emailLabel", defaultMessage: "Email" },
	invalidEmail: { id: "registration.invalidEmail", defaultMessage: "Invalid email address" },
	passwordLabel: { id: "registration.passwordLabel", defaultMessage: "Password" },
	invalidPassword: { id: "registration.invalidPassword", defaultMessage: "Invalid password" },
	agreeTermsLabel: { id: "registration.agreeTermsLabel", defaultMessage: "I agree to the" },
	termsOfService: { id: "registration.termsOfService", defaultMessage: "terms of service" },
	termsError: { id: "registration.termsError", defaultMessage: "You must accept the terms" },
	agreeNewsletterLabel: { id: "registration.agreeNewsletterLabel", defaultMessage: "I agree to receive commercial information and news" },
	registering: { id: "registration.registering", defaultMessage: "Registering..." },
	registerButton: { id: "registration.registerButton", defaultMessage: "Register" },
	// External service registration
	externalServiceTitle: { id: "registration.externalServiceTitle", defaultMessage: "With external service" },
	googleSignup: { id: "registration.googleSignup", defaultMessage: "Register with Google" },
	microsoftSignup: { id: "registration.microsoftSignup", defaultMessage: "Register with Microsoft" },
	// Login link
	alreadyHaveAccount: { id: "registration.alreadyHaveAccount", defaultMessage: "Already have an account?" },
	loginLink: { id: "registration.loginLink", defaultMessage: "Login" },
	// Default announcement
	oneStepAway: { id: "registration.oneStepAway", defaultMessage: "You're one step away from the world of integrations." },
	hundredsOfApps: { id: "registration.hundredsOfApps", defaultMessage: "Hundreds of different connections and application add-ons." },
	timeAndMoneySaving: { id: "registration.timeAndMoneySaving", defaultMessage: "Smart scenarios saving time, work, and money." },
	freeTrial: { id: "registration.freeTrial", defaultMessage: "14 days to try them for free." },
});

export function isRegistration() {
	if (window.location.pathname === "/signup" || window.location.pathname === "/registration") {
		return true;
	}
	return false;
}

export function init() {
	let finishedPromise = new Deferred();
	ReactDOM.render(
		<IntlProvider locale={getLocale()} messages={getMessages()}>
			<Registration />
		</IntlProvider>,
		document.getElementById("app")
	);

	return finishedPromise.promise;
}

interface Registration_Props {
	intl: IntlShape;
}

class Registration_ extends React.Component<Registration_Props> {
	static propTypes = {
		intl: PropTypes.object.isRequired,
	};

	constructor() {
		super();

		this.state = {
			step: 1,
			apps: "",
			newsletter: true,
			isFromLogin: window.location.hash.indexOf("#pathname") === 0,
			registered: false,
			email: "",
			password: "",
			termsAccepted: false,
			agreeTerms: false, // Add this state property to track the checkbox
			emailError: false,
			passwordError: false,
			termsError: false,
			signupError: null,
			isLoading: false,
			showPassword: false,
			announcement: null, // Add state for announcement data
			announcementLoading: true, // Track if announcement is loading
		};

		this.skipInstallStep = false;

		this.handleChange = this.handleChange.bind(this);
		this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
		this.togglePasswordVisibility = this.togglePasswordVisibility.bind(this);
		this.handleLocalSignUp = this.handleLocalSignUp.bind(this);
	}

	handleChange(event) {
		const { name, value } = event.target;
		this.setState({ [name]: value });
		this.handleInputChange(event);
	}

	handleCheckboxChange(event) {
		const { name, checked } = event.target;
		this.setState({ [name]: checked });

		if (name === "agreeTerms") {
			// Update both state properties in sync
			this.setState({ 
				termsAccepted: checked,
			});
			
			const mappedEvent = {
				...event,
				target: { ...event.target, name: "termsAccepted" },
			};
			this.handleInputChange(mappedEvent);
		}

		if (name === "agreeNews") {
			this.newsletterChange(event);
		}
	}

	togglePasswordVisibility() {
		this.setState((prevState) => ({ showPassword: !prevState.showPassword }));
	}

	handleLocalSignUp(event) {
		event.preventDefault();
		if (!this.state.termsAccepted && this.state.agreeTerms) {
			const syntheticEvent = {
				target: { name: "termsAccepted", checked: this.state.agreeTerms },
			};
			this.handleInputChange(syntheticEvent);
		}
		this.handleSignUp(event);
	}

	getLogInPath() {
		let hash = window.location.hash || "";
		let path = hash.substring("#pathname=".length);

		return path || "/";
	}

	render() {
		return (
			<div className="Registration" >
				<header>
					<a href="https://www.dativery.com">
						<Logo color />
					</a>
				</header>
				{this.renderCreateAccount()}
			</div>
		);
	}

	initAuth() {
		this.auth0Client = new auth0.WebAuth({
			domain: Auth0Variables.AUTH0_DOMAIN,
			clientID: Auth0Variables.AUTH0_CLIENT_ID,
			responseType: "token id_token",
			redirectUri: window.location.origin + "/login?pathname=" + encodeURIComponent(window.location.query?.pathname || window.location.pathname),
		});
	}

	getSubscribeLink() {
		return "https://www.dativery.com/" + getLocale() + "/stay-informed-subscribe/";
	}

	getPrivacyLink() {
		return "https://www.dativery.com/" + getLocale() + "/privacy/";
	}

	componentDidMount() {
		this.initAuth();
		this.fetchAnnouncement();
	}

	fetchAnnouncement = () => {
		fetch(config.GLOBAL_API_URL + '/login-announcement')
			.then(response => {
				if (!response.ok) {
					throw new Error('Network response was not ok');
				}
				return response.json();
			})
			.then(data => {
				this.setState({
					announcement: data,
					announcementLoading: false
				});
			})
			.catch(error => {
				console.error('Error fetching announcement:', error);
				this.setState({ announcementLoading: false });
			});
	};

	newsletterChange(event) {
		this.setState({
			newsletter: event.target.checked,
		});
	}

	handleInputChange = (event) => {
		const { name, value, type, checked } = event.target;
		this.setState({
			[name]: type === "checkbox" ? checked : value,
		});
	};

	handleSignUp = (event) => {
		event.preventDefault();

		this.setState({
			emailError: false,
			passwordError: false,
			termsError: false,
			isLoading: true,
		});

		this.auth0Client.signup(
			{
				email: this.state.email,
				password: this.state.password,
				connection: "Username-Password-Authentication",
			},
			(err, result) => {
				if (err) {
					this.setState({
						isLoading: false,
						signupError: err.description || "An error occurred during signup",
					});
					return;
				}

				this.auth0Client.login(
					{
						email: this.state.email,
						password: this.state.password,
						realm: "Username-Password-Authentication",
						responseType: "token id_token",
					},
					(err, authResult) => {
						if (err) {
							this.setState({
								isLoading: false,
								loginError: err.description || "Error during login",
							});
							return;
						}

						this.setState({ registered: true });

						this.auth0Client.client.userInfo(authResult.accessToken, (err, profile) => {
							if (err) {
								displayError(err);
								return;
							}

							if (this.state.newsletter) {
								this.newsletterSubscribe({
									LANG: getLocale() === "cs" ? "cs" : "en",
									EMAIL: profile.email,
									FNAME: profile.given_name,
									LNAME: profile.family_name,
								});
							}

							localStorage.setItem("profile", JSON.stringify(profile));
							localStorage.setItem("userToken", authResult.idToken);

							let RelayNetworkLayer = initRelayLayer();
							RelayNetworkLayer.sendQueries([new RelayQueryRequest(Query(MeQuery.user()))])
								.then(() => {
									this.finishRegistrion();
								})
								.catch(() => {
									localStorage.removeItem("profile");
									localStorage.removeItem("userToken");
								});
						});
					}
				);
			}
		);
	};

	handleSocialLogin = (connection) => {
		this.auth0Client.authorize({
			connection,
			responseType: "token id_token",
			redirectUri: window.location.origin + "/login?pathname=" + encodeURIComponent(window.location.query?.pathname || window.location.pathname),
		});
	};

	renderCreateAccount() {
		let isActive = true;
		const { announcement, announcementLoading } = this.state;

		return (
			<div
				style={{
					display: "flex",
					minHeight: "100vh",
					width: "100%",
					marginTop: "-3rem",
				}}
			>
				<main style={{ flex: "1", display: "flex", backgroundColor: "#f9fafb" }}>
					<div style={{ display: "flex", width: "100%" }}>
						<div style={{ width: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
							<div style={{ width: "100%", maxWidth: "448px" }}>
								<div>
									<div className="register-registration-container">
										<div className="register-form-section register-email-section">
											<h2>{this.props.intl.formatMessage(messages.emailRegistrationTitle)}</h2>

											<form onSubmit={this.handleLocalSignUp}>
												<div className="register-form-group">
													<label htmlFor="email">{this.props.intl.formatMessage(messages.emailLabel)}</label>
													<div className="register-input-wrapper">
														<span className="register-input-icon">
															<i className="fa fa-envelope" aria-hidden="true"></i>
														</span>
														<input type="email" id="email" name="email" value={this.state.email} onChange={this.handleChange} required className={this.state.emailError ? "error-input" : ""} />
													</div>
													{this.state.emailError && <p className="error-message">{typeof this.state.emailError === "string" ? this.state.emailError : this.props.intl.formatMessage(messages.invalidEmail)}</p>}
												</div>

												<div className="register-form-group">
													<label htmlFor="password">{this.props.intl.formatMessage(messages.passwordLabel)}</label>
													<div className="register-input-wrapper">
														<span className="register-input-icon">
															<i className="fa fa-lock" aria-hidden="true"></i>
														</span>
														<input type={this.state.showPassword ? "text" : "password"} id="password" name="password" value={this.state.password} onChange={this.handleChange} required className={this.state.passwordError ? "error-input" : ""} minLength={8} />
														<button type="button" className="register-input-icon-right" onClick={this.togglePasswordVisibility}>
															<i className={`fa ${this.state.showPassword ? "fa-eye-slash" : "fa-eye"}`} aria-hidden="true"></i>
														</button>
													</div>
													{this.state.passwordError && <p className="error-message">{typeof this.state.passwordError === "string" ? this.state.passwordError : this.props.intl.formatMessage(messages.invalidPassword)}</p>}
												</div>

												<div className="register-checkbox-group">
													<input type="checkbox" id="agreeTerms" name="agreeTerms" checked={this.state.agreeTerms} onChange={this.handleCheckboxChange} required />
													<label htmlFor="agreeTerms">
														{this.props.intl.formatMessage(messages.agreeTermsLabel)}{" "}
														<a href={`https://www.dativery.com/cs/terms/`} target="_blank" rel="noopener noreferrer">
															{this.props.intl.formatMessage(messages.termsOfService)}
														</a>
													</label>
													{this.state.termsError && <p className="error-message">{this.props.intl.formatMessage(messages.termsError)}</p>}
												</div>

												<div className="register-checkbox-group">
													<input type="checkbox" id="agreeNews" name="agreeNews" checked={this.state.newsletter} onChange={this.handleCheckboxChange} />
													<label htmlFor="agreeNews">{this.props.intl.formatMessage(messages.agreeNewsletterLabel)}</label>
												</div>

												{this.state.signupError && <div className="error-banner">{this.state.signupError}</div>}

												<button type="submit" className={`register-btn register-submit-btn ${!this.state.agreeTerms || this.state.isLoading ? "register-disabled-btn" : ""}`} disabled={!this.state.agreeTerms || this.state.isLoading}>
													{this.state.isLoading ? this.props.intl.formatMessage(messages.registering) : this.props.intl.formatMessage(messages.registerButton)}
												</button>
											</form>
										</div>

										<div className="register-form-section register-external-section">
											<h2>{this.props.intl.formatMessage(messages.externalServiceTitle)}</h2>
											<button type="button" className="register-btn register-external-btn register-google-btn" onClick={() => this.handleSocialLogin("google-oauth2")}>
												<span className="register-btn-icon">G</span> {this.props.intl.formatMessage(messages.googleSignup)}
											</button>
											<button type="button" className="register-btn register-external-btn register-microsoft-btn" onClick={() => this.handleSocialLogin("windowslive")}>
												<span className="register-btn-icon">M</span> {this.props.intl.formatMessage(messages.microsoftSignup)}
											</button>
										</div>
									</div>
									<div className="register-login-link">
										<p>
											{this.props.intl.formatMessage(messages.alreadyHaveAccount)} <a href="/login">{this.props.intl.formatMessage(messages.loginLink)}</a>
										</p>
									</div>
								</div>
							</div>
						</div>

						<div style={{ width: "50%", position: "relative" }}>
							<div
								style={{
									position: "absolute",
									top: 0,
									left: 0,
									width: "100%",
									height: "100%",
									backgroundImage: announcement ? `url('${announcement.backgroundImage}')` : "url('https://img.freepik.com/free-photo/showing-cart-trolley-shopping-online-sign-graphic_53876-133967.jpg')",
									backgroundSize: "cover",
									backgroundPosition: "center",
									filter: "brightness(50%)",
								}}
							></div>
							<div
								style={{
									position: "absolute",
									inset: "0",
									marginInline: "auto",
									width: "fit-content",
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									color: "white",
									padding: "2rem",
									textAlign: "center",
									overflowY: "auto",
									maxWidth: "70em",
									padding: "8em",
									textAlign: "left",
									overflowY: "auto",
								}}
							>
								{announcementLoading ? (
									<LoadingLine />
								) : announcement ? (
									<div style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", padding: "2em", borderRadius: "8px" }}>
										<h2
											style={{
												fontSize: "1.875rem",
												fontWeight: "bold",
												marginBottom: "1rem",
												marginBottom: "1.5em",
											}}
										>
											{announcement.title}
										</h2>
										<div
											className="registration-announcement-text"
											style={{
												marginBottom: "1.5em",
											}}
											dangerouslySetInnerHTML={{ __html: announcement.body }}
										/>
										{announcement.link && (
											<div style={{ textAlign: "center" }}>
												<a
													href={announcement.link}
													target="_blank"
													rel="noopener noreferrer"
													style={{
														display: "inline-block",
														backgroundColor: "rgba(255, 255, 255, 0.2)",
														color: "white",
														marginTop: "1.5em",
														padding: "0.5rem 1rem",
														borderRadius: "0.25rem",
														textDecoration: "none",
														fontWeight: "500",
														backdropFilter: "blur(4px)",
														border: "1px solid rgba(255, 255, 255, 0.3)",
														transition: "all 0.3s ease",
													}}
													onMouseOver={(e) => (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.3)")}
													onMouseOut={(e) => (e.target.style.backgroundColor = "rgba(255, 255, 255, 0.2)")}
												>
													{announcement.linkText || "Více informací"}
												</a>
											</div>
										)}
									</div>
								) : (
									<>
										<h2
											style={{
												fontSize: "1.875rem",
												fontWeight: "bold",
												marginBottom: "1rem",
											}}
										>
											{this.props.intl.formatMessage(messages.oneStepAway)}
										</h2>
										<p style={{ marginBottom: "0.5rem", textShadow: "1px 1px 1px rgba(0, 0, 0, 0.5)" }}>{this.props.intl.formatMessage(messages.hundredsOfApps)}</p>
										<p style={{ marginBottom: "0.5rem", textShadow: "1px 1px 1px rgba(0, 0, 0, 0.5)" }}>{this.props.intl.formatMessage(messages.timeAndMoneySaving)}</p>
										<p
											style={{
												fontSize: "1.25rem",
												fontWeight: "600",
												textShadow: "1px 1px 1px rgba(0, 0, 0, 0.5)",
											}}
										>
											{this.props.intl.formatMessage(messages.freeTrial)}
										</p>
									</>
								)}
							</div>
						</div>
					</div>
				</main>
			</div>
		);
	}

	newsletterSubscribe(params) {
		EventTracker.trackEvent(EventTracker.EVENT_CLICKS, {
			event: "newsletter",
			type: "application",
		});

		let formData = new FormData();

		formData.append("email", params.EMAIL);
		formData.append("name", params.FNAME);
		formData.append("surname", params.LNAME);

		return fetch("https://dativery.ecomailapp.cz/public/subscribe/20/85f7dba955e715936a175d446bc6f115?source=app", {
			method: "POST",
			headers: {
				"Content-Type": "multipart/form-data",
			},
			body: formData,
		}).catch((e) => {
			console.error(e);
		});
	}

	finishRegistrion() {
		if (this.state.isFromLogin) {
			window.location = window.location.origin + decodeURI(window.location.hash.substring(10));
		} else {
			window.location = window.location.origin + "/s/";
		}
	}
}
let Registration = injectIntl(Registration_);
